import { UpdateCompanyClientData } from '@esg/syntiro-api-core';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Drawer,
  Switch,
  Theme,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import { Company, Group } from '@esg/esg-global-types';
import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { DataGrid, GridActionsCellItem, GridColDef } from '@mui/x-data-grid';
import AddCompanyWidget from './AddCompanyWidget';
import DeleteConfirmModal from '../../shared/modal/DeleteConfirmModal';
import { CompanyExtended, company_label } from '../../../lib/app/company';
import { ReactComponent as ConfigurationIcon } from '../../../assets/icons/Configuration-09.svg';
import { ReactComponent as DeleteIcon } from '../../../assets/icons/Delete Outlined.svg';
import { generateUuidV4 } from '../../../util/uuid';
import { log } from '../../../util/log';
import { MetadataError } from '@ep/error-handling';
import { useNavigate } from 'react-router-dom';
import { CompanyContext } from '../../../context/CompanyContext';
import { GroupContext } from '../../../context/GroupContext';
import GhostButton from '../../shared/button/GhostButton';
import PrimaryButton from '../../shared/button/PrimaryButton';
import DataGridNoRows from '../../shared/datagrid/DataGridNoRows';
import DataGridLoading from '../../shared/datagrid/DataGridLoading';
import DataGridToolbar from '../../shared/datagrid/DataGridToolbar';
import { softDeleteCompany, updateCompany } from '../../../graphql/services/company.service';
import { useContext, useState } from 'react';
import { FEEDBACK_ACTION_FAILURE, FUNCTION_UNKNOWN_FAILURE } from '../../../util/global_variables';

const SOURCE = 'PanelCompanies.tsx';

/**
 * Datagrid for viewing and creating Companies for a previously selected Group
 * @param {Group} group Parent Group of the Companies in the datagrid
 * @param {void} addGroupCompany Function to add a newly created company in memory to be displayed within the app
 * @param {void} removeGroupCompany Function to remove a newly deleted company in memory to be displayed within the app
 * @returns {JSX.Element}
 */
const PanelCompanies = ({
  group,
  addGroupCompany,
  removeGroupCompany
}: {
  group: Group;
  addGroupCompany: (new_company: CompanyExtended) => void;
  removeGroupCompany: (delted_company: Company) => void;
}) => {
  const { setFeedbackData } = useContext(FeedbackSnackbarContext);
  const { handleCompanyChange } = useContext(CompanyContext);
  const { handleGroupChange } = useContext(GroupContext);
  const [actionRow, setActionRow] = useState<Company | null>(null);
  const [companyRows, setCompanyRows] = useState<Array<Company>>(group.companies ?? []);
  const [displayWidgetPanelAdd, setDisplayWidgetPanelAdd] = useState<boolean>(false);
  const [openDialogActive, setOpenDialogActive] = useState<boolean>(false);
  const [openDialogDelete, setOpenDialogDelete] = useState<boolean>(false);
  const [actionLoading, setActionLoading] = useState<boolean>(false);
  const theme: Theme = useTheme();
  const primary_blue: string = theme.palette.primary.main;

  const navigate = useNavigate();

  // Handler functions
  const handleCreateClick = (): void => {
    setActionRow(null);
    setDisplayWidgetPanelAdd(true);
  };

  const handleActiveToggleClick = (row: Company): void => {
    setActionRow(row);
    setOpenDialogActive(true);
  };

  const handleUpdateRows = (rows_data: Array<Company>): void => {
    setCompanyRows(rows_data);
  };

  const handleEditCompany = (updated_row: Company): void => {
    const new_rows: Array<Company> = companyRows.map((row) =>
      updated_row.id === row.id ? { ...updated_row } : row
    );
    setCompanyRows(new_rows);
  };

  const handleCloseActiveModal = (): void => {
    setOpenDialogActive(false);
  };

  const handleCompanyActive = async (new_active: boolean): Promise<void> => {
    try {
      if (actionRow) {
        setActionLoading(true);
        const update_company_client_data: UpdateCompanyClientData = {
          ...actionRow
        };
        await updateCompany(
          {
            group_id: group.id
          },
          update_company_client_data
        ).then(() => {
          handleEditCompany({ ...actionRow, active: new_active });
        });
      }
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: PanelCompanies failed on an unknown error while calling handleCompanyActive.',
          {
            actionRow: actionRow,
            new_active: new_active
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `An error occurred. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    } finally {
      handleCloseActiveModal();
      setActionLoading(false);
    }
  };

  const handleConfigCompany = (row: Company): void => {
    handleGroupChange(group);
    handleCompanyChange(row);
    navigate('/configuration');
  };

  const handleDeleteClick = (row: Company): void => {
    setActionRow(row);
    setOpenDialogDelete(true);
  };

  const handleCloseDeleteModal = (): void => {
    setOpenDialogDelete(false);
  };

  const deleteRow = async (company: Company) => {
    try {
      await softDeleteCompany({ group_id: group.id }, { id: company.id });
      handleUpdateRows(
        companyRows.filter((row) => {
          return row.id !== company.id;
        })
      );
      removeGroupCompany(company);
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'deleteRow'),
          {
            group_id: group?.id,
            company_id: company?.id
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: FEEDBACK_ACTION_FAILURE('Unable to delete company', tracking_id),
        state: true,
        type: 'error'
      });
    }
  };

  const columns: Array<GridColDef> = [
    {
      field: 'active',
      headerName: 'Active',
      headerAlign: 'left',
      align: 'left',
      flex: 1,
      renderCell: (params) => {
        return (
          <Switch
            checked={params.row.active ? true : false}
            onChange={() => {
              setActionRow(params.row);
              handleActiveToggleClick(params.row);
            }}
          />
        );
      }
    },
    {
      field: 'logo',
      headerName: 'Logo',
      headerAlign: 'left',
      align: 'left',
      renderCell: (params) => {
        return <img width={56} src={params.row.configuration.logo as string} alt="logo" />;
      },
      flex: 1
    },
    {
      field: 'name',
      headerName: 'Name',
      headerAlign: 'left',
      align: 'left',
      flex: 1
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: '',
      headerAlign: 'left',
      align: 'left',
      hideable: false,
      flex: 1,
      getActions: ({ row }) => {
        return [
          <>
            <GridActionsCellItem
              key={1}
              icon={
                <Tooltip title="Configure Company">
                  <ConfigurationIcon height={30} fill={primary_blue} stroke={primary_blue} />
                </Tooltip>
              }
              size="large"
              label="Edit"
              sx={{
                color: 'primary.main'
              }}
              onClick={() => handleConfigCompany(row)}
              disabled={!row.active}
            />
            <GridActionsCellItem
              key={2}
              icon={
                <Tooltip title="Delete Company">
                  <DeleteIcon height={30} fill={primary_blue} stroke={primary_blue} />
                </Tooltip>
              }
              size="large"
              label="Delete"
              sx={{
                color: 'primary.main'
              }}
              onClick={() => handleDeleteClick(row)}
              disabled={!row.active}
            />
          </>
        ];
      }
    }
  ];

  return (
    <>
      {/* Set Company Active Modal */}
      <Dialog open={openDialogActive} onClose={handleCloseActiveModal}>
        <Box sx={{ p: 1.5 }}>
          <DialogTitle id="alert-dialog-title">
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap'
              }}
            >
              <WarningAmberIcon sx={{ marginRight: '1rem' }} fontSize="medium" />
              <Typography variant="h6" color="inherit" align="left">
                {actionRow?.active ? 'Disable' : 'Enable'}{' '}
                {actionRow?.name ? actionRow.name : 'Company'}?
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              You are about to {actionRow?.active ? 'deactivate' : 'activate'}{' '}
              {actionRow?.name ? actionRow.name : 'company'} {actionRow?.active ? 'from' : 'for'}{' '}
              your companies.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <GhostButton onClick={handleCloseActiveModal} outlined dark>
              Back
            </GhostButton>
            <PrimaryButton
              loading={actionLoading}
              onClick={() => actionRow && handleCompanyActive(actionRow.active ? false : true)}
              palette_color="error"
              autoFocus
            >
              Confirm
            </PrimaryButton>
          </DialogActions>
        </Box>
      </Dialog>

      <DeleteConfirmModal
        open={openDialogDelete}
        allow_delete={true}
        delete_warning="This company will no longer be accessible through the platform."
        handleCloseDeleteModal={handleCloseDeleteModal}
        delete_label={actionRow && actionRow.name}
        handleDelete={actionRow && (() => deleteRow(actionRow))}
        allow_archive={false}
      />

      {/* Side Widget Panel */}
      <Drawer
        anchor={'right'}
        open={displayWidgetPanelAdd}
        onClose={() => setDisplayWidgetPanelAdd(false)}
        PaperProps={{ style: { width: '30%', padding: '1.5rem' } }}
      >
        <AddCompanyWidget
          group={group}
          handleCompanyToParent={(new_company: CompanyExtended) => {
            addGroupCompany(new_company);
            handleUpdateRows([new_company, ...companyRows]);
            setDisplayWidgetPanelAdd(false);
          }}
        />
      </Drawer>

      {/* Interactive Data Table */}
      <DataGrid
        autoHeight
        rowHeight={65}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 10, page: 0 }
          },
          sorting: {
            sortModel: [{ field: 'modified', sort: 'desc' }]
          }
        }}
        hideFooter={companyRows.length > 1 ? false : true}
        columnHeaderHeight={companyRows.length < 1 ? 0 : 56}
        pageSizeOptions={[10, 25, 50, 100]}
        rows={companyRows}
        columns={columns}
        disableRowSelectionOnClick
        slots={{
          toolbar: () => (
            <DataGridToolbar entity_label={company_label} handleCreate={handleCreateClick} />
          ),
          noRowsOverlay: () => <DataGridNoRows missing_label={company_label} />,
          loadingOverlay: () => <DataGridLoading columns={columns.length} />
        }}
        slotProps={{ toolbar: { handleCreate: handleCreateClick } }}
        sx={{ '&, [class^=MuiDataGrid]': { border: 'none' }, width: '100%' }}
      />
    </>
  );
};

export default PanelCompanies;
